import { useMemo, useState } from "react";
import type { AdminAreaSuggestionView, AreaSuggestionStatus } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import {
  Button,
  DataTable,
  EmptyView,
  ErrorState,
  LoadingState,
  StatusDot,
  Tabs,
  Tag,
  type Column,
  type DotTone,
  type TabItem,
} from "@/shared/rd";
import { useAreaSuggestionsQuery } from "../services/useAreaSuggestionsQuery";
import { useAreasForDuplicateHintQuery } from "../services/useAreasForDuplicateHintQuery";
import { looksLikeDuplicate } from "../lib/duplicate-hint";
import { AreaSuggestionActions } from "../components/AreaSuggestionActions";

type StatusFilter = AreaSuggestionStatus | "all";

const STATUS_TONE: Record<AreaSuggestionStatus, DotTone> = {
  pending: "blue",
  approved: "green",
  rejected: "red",
};

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale === "ar" ? "ar-EG" : "en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

type Props = {
  locale: string;
};

// Admin queue for buyer-typed "custom area" names that had no catalog match.
// Default filter is "pending", sorted by occurrence count descending (the
// API's default sort) — the most-requested missing area surfaces first.
export function AreaSuggestionsPage({ locale }: Props) {
  const t = useTranslations();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");

  const { data: suggestions, isLoading, error, refetch } = useAreaSuggestionsQuery(
    statusFilter === "all" ? undefined : statusFilter,
  );
  // The Pending tab's count, whichever tab is open (same query key as the
  // pending tab, so it is one request, cached).
  const { data: pending } = useAreaSuggestionsQuery("pending");

  // Scope the duplicate-hint comparison set to only the governorates that
  // actually appear in the current suggestion list — see
  // useAreasForDuplicateHintQuery for why this replaces a full-catalog fetch.
  const suggestionGovernorateIds = useMemo(
    () => (suggestions ?? []).map((s) => s.governorateId),
    [suggestions],
  );
  const { data: existingAreas } = useAreasForDuplicateHintQuery(suggestionGovernorateIds);

  const statusLabel = (status: AreaSuggestionStatus): string =>
    status === "pending"
      ? t(keys.areaSuggestions.statusPending)
      : status === "approved"
        ? t(keys.areaSuggestions.statusApproved)
        : t(keys.areaSuggestions.statusRejected);

  const number = (n: number) => n.toLocaleString(locale === "ar" ? "ar-EG" : "en-US");

  const columns: Array<Column<AdminAreaSuggestionView>> = [
    {
      key: "rawName",
      label: t(keys.areaSuggestions.columnRawName),
      width: 1.6,
      render: (s) => (
        <bdi dir="auto" style={{ fontWeight: 500 }}>
          {s.rawName}
        </bdi>
      ),
    },
    {
      key: "governorate",
      label: t(keys.areaSuggestions.columnGovernorate),
      width: 1,
      render: (s) => (locale === "ar" ? s.governorateNameAr : (s.governorateNameEn ?? s.governorateNameAr)),
    },
    {
      key: "occurrenceCount",
      label: t(keys.areaSuggestions.columnOccurrenceCount),
      width: 0.8,
      align: "end",
      numeric: true,
      render: (s) => number(s.occurrenceCount),
    },
    {
      key: "lastSeenAt",
      label: t(keys.areaSuggestions.columnLastSeen),
      width: 1,
      align: "end",
      numeric: true,
      render: (s) => <span style={{ color: "var(--rd-text-secondary)" }}>{formatDate(s.lastSeenAt, locale)}</span>,
    },
    {
      key: "status",
      label: t(keys.areaSuggestions.columnStatus),
      width: 1.7,
      render: (s) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <StatusDot tone={STATUS_TONE[s.status]}>{statusLabel(s.status)}</StatusDot>
          {s.status === "pending" && existingAreas && looksLikeDuplicate(s, existingAreas) ? (
            <Tag tone="amber" title={t(keys.areaSuggestions.possibleDuplicateHint)}>
              {t("areaSuggestions.similarAreaTag")}
            </Tag>
          ) : null}
        </span>
      ),
    },
    {
      key: "actions",
      label: "",
      width: "96px",
      align: "end",
      render: (s) => (s.status === "pending" ? <AreaSuggestionActions suggestion={s} /> : null),
    },
  ];

  // Tabs, as the design draws the queue's status filter.
  const tabItems: TabItem<StatusFilter>[] = [
    { value: "pending", label: t(keys.areaSuggestions.filterPending), count: pending?.length },
    { value: "approved", label: t(keys.areaSuggestions.filterApproved) },
    { value: "rejected", label: t(keys.areaSuggestions.filterRejected) },
    { value: "all", label: t(keys.areaSuggestions.filterAll) },
  ];

  // Per-tab empty state, as the canvas's EmptyState board draws it: what is
  // missing, when it fills, and the one tab worth opening instead.
  const empty: Record<StatusFilter, { title: string; sub: string; action: string; target: StatusFilter }> = {
    pending: {
      title: t("areaSuggestions.emptyPendingTitle"),
      sub: t("areaSuggestions.emptyPendingSub"),
      action: t("areaSuggestions.emptySeeApproved"),
      target: "approved",
    },
    approved: {
      title: t("areaSuggestions.emptyApprovedTitle"),
      sub: t("areaSuggestions.emptyApprovedSub"),
      action: t("areaSuggestions.emptyBackToPending"),
      target: "pending",
    },
    rejected: {
      title: t("areaSuggestions.emptyRejectedTitle"),
      sub: t("areaSuggestions.emptyRejectedSub"),
      action: t("areaSuggestions.emptyBackToPending"),
      target: "pending",
    },
    all: {
      title: t("areaSuggestions.emptyAllTitle"),
      sub: t("areaSuggestions.emptyAllSub"),
      action: t("areaSuggestions.emptyBackToPending"),
      target: "pending",
    },
  };
  const emptyView = empty[statusFilter];
  const page = t("areaSuggestions.pageName");

  return (
    <>
      <Tabs tabs={tabItems} value={statusFilter} onChange={setStatusFilter} label={t(keys.areaSuggestions.columnStatus)} />


      {isLoading ? (
        <LoadingState title={t(keys.shell.loadingTitle, { page })} sub={t(keys.shell.loadingSub)} />
      ) : error || !suggestions ? (
        <ErrorState
          title={t(keys.shell.errorTitle, { page })}
          sub={t(keys.shell.errorSub)}
          retryLabel={t(keys.shell.retry)}
          onRetry={() => void refetch()}
        />
      ) : suggestions.length === 0 ? (
        <EmptyView
          title={emptyView.title}
          sub={emptyView.sub}
          action={
            <Button variant="secondary" size="sm" onClick={() => setStatusFilter(emptyView.target)}>
              {emptyView.action}
            </Button>
          }
        />
      ) : (
        <DataTable
          label={t(keys.areaSuggestions.title)}
          columns={columns}
          rows={suggestions}
          rowKey={(s) => s.id}
          minWidth={820}
          empty={emptyView.title}
        />
      )}
    </>
  );
}
